import { createContext, useContext, useEffect, useRef, useState, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { trace, traceWho } from '../lib/trace';
import { logAudit } from '../hooks/useAudit';
import IdleWarning from './IdleWarning';

const AuthContext = createContext(null);

// Thrown when the password was right but the account is not allowed in.
// Carries a code so the login screen can say something more useful than
// "check your password" to somebody whose password is fine.
export class AccountInactiveError extends Error {
  constructor(message) {
    super(message);
    this.name = 'AccountInactiveError';
    this.code = 'ACCOUNT_INACTIVE';
  }
}

// Supabase Auth does not know profiles.is_active exists, so a password alone
// still gets you a session. This is the gate that turns a valid password into
// an actual login - and it runs on session restore too, so switching somebody
// off takes effect the next time their browser loads the app rather than
// whenever their token happens to expire.
function isAllowedIn(prof) {
  return !!prof && prof.is_active === true;
}

/*
  What to do with the answer to a profile load, as one decision in one place.

  **Three outcomes, and they used to be two.** Written 7 September 2026 after a
  read that came back 401 was treated as "this person is not allowed in", which
  signed the owner out a moment after he had successfully signed in.

    'in'      the profile loaded and the account is allowed in
    'out'     the profile loaded and the account is NOT allowed in: sign out
    'leave'   nothing was learned. The read did not work, or it answered a
              question that has since been asked again. Change nothing.

  Exported so it can be tested without a browser, a database or a component,
  because the fault it fixes is a fault of judgement rather than of plumbing.
*/
export function whatToDo({ profile, failed, stale }) {
  if (failed || stale) return 'leave';
  /*
    **No row is not the same as no person, and this is what the first fix got
    wrong.** Corrected 7 September 2026, after the owner reported the same fault
    a second time.

    The database hides a person's own record from a request that arrives without
    a usable token: not with an error, but by answering **no rows**, which is
    exactly what it answers for somebody who genuinely has no record. Several of
    these reads fire at once on every sign-in and at least one of them goes out
    before the browser has the new token, so an empty answer arrives on every
    single sign-in.

    The first fix separated "the read broke" from "no such person" and then
    signed him out for the second, which is the one that was actually happening.

    **So being switched off is now the only thing that signs anybody out**, and
    that is a fact read off a row. An absence is not a fact: it is asked again.
  */
  if (!profile) return 'again';
  return isAllowedIn(profile) ? 'in' : 'out';
}

/*
  **Being signed out for doing nothing, and why it never happened.**

  Rewritten 8 September 2026, after the owner said he signed in one evening,
  left the tab open all night, and was still signed in the next morning, and
  that he had never once seen the two minute warning either.

  **Both were true and it was one fault.** The old version set two alarm clocks,
  one for the warning and one for the sign-out, and started them again from
  scratch every time `user` changed. `user` is the object Supabase hands over
  with the session, and it hands over a NEW one every time it renews the token,
  which it does by itself a little before every hour. So the hour-long alarm was
  wound back to the start at about fifty-eight and a half minutes, every hour,
  for ever. The warning had about thirty seconds to be noticed before it was
  wiped as well.

  **Two things are different now.**

  It watches a clock rather than setting an alarm. The moment of the last thing
  anybody did is written down, and every second the app asks how long ago that
  was. Renewing a token is not something a person did, so it changes nothing.
  **And a machine that was asleep is handled by the same fact**: an alarm set for
  two hours does not go off while a laptop is shut, but a clock still says what
  time it is when the lid opens, so the sign-out happens on waking rather than
  two hours later.

  And the moment is shared between tabs, so OnHand open twice is one session
  doing nothing rather than two.

  **Two hours, warning at two minutes, on the owner's instruction of 8 September
  2026.** It used to be an hour.
*/
const IDLE_TIMEOUT_MS  = 2 * 60 * 60 * 1000; // two hours of nobody doing anything
const WARN_BEFORE_MS   =     2 * 60 * 1000; // the warning shows for the last two minutes
const IDLE_TICK_MS     =          1 * 1000; // how often the clock is looked at
const ACTIVITY_THROTTLE_MS =      1 * 1000; // moving a mouse writes this down at most once a second

// Where the moment of the last activity is kept, so every tab of OnHand shares
// one answer to "when did somebody last do something".
const ACTIVITY_KEY = 'onhand:lastActivity';

const IDLE_EVENTS = ['mousemove', 'mousedown', 'keydown', 'touchstart', 'scroll', 'click'];

// How long the warning says is left, in the words the modal uses.
export function idleWarningWords(timeoutMs = IDLE_TIMEOUT_MS) {
  const mins = Math.round(timeoutMs / 60000);
  if (mins % 60 === 0) {
    const hrs = mins / 60;
    return hrs === 1 ? 'an hour' : hrs + ' hours';
  }
  return mins + ' minutes';
}

/* What a sign-out is recorded as: a written reason, or an ordinary sign-out. */
export function signOutReason(given) {
  return typeof given === 'string' && given ? given : 'USER_LOGOUT';
}

export function AuthProvider({ children }) {
  const [user, setUser]           = useState(null);
  const [profile, setProfile]     = useState(null);
  const [loading, setLoading]     = useState(true);
  const [showWarning, setShowWarning] = useState(false);
  /* Set when somebody arrives on a password reset link. See the note beside
     PASSWORD_RECOVERY below. */
  const [recovering, setRecovering] = useState(false);

  // Keep a ref to profile so signOut callback can always access latest value
  useEffect(() => { profileRef.current = profile; }, [profile]);
  const [countdown, setCountdown] = useState(Math.round(WARN_BEFORE_MS / 1000)); // seconds left, shown in the warning

  /*
    **How many reads of the person's record are still out.** Added 8 September
    2026, and it is the whole of the flash the owner filmed.

    Between "Supabase says you are signed in" and "OnHand has your record" there
    is a gap of a second or so. `App` shows the screen that says **"You are
    signed in, but OnHand could not load your account"** whenever there is a
    session and no record, and it could not tell that gap apart from a read that
    had finished and found nothing. So the alarming screen flashed up on every
    single sign-in, in front of a sign-in that was working perfectly.

    Counting the reads that are still out separates the two. A read on its way
    is not a read that failed.
  */
  const [profileLoads, setProfileLoads] = useState(0);

  /*
    **A read that is about to be made counts too.** Two of these reads are
    handed to a timer rather than started straight away, because Supabase must
    not be asked anything from inside its own callback. That leaves a moment
    where OnHand knows there is a session, has no record yet, and has not yet
    started asking, and that moment is a whole drawing of the screen: long
    enough for the alarming screen to appear and vanish, which is what the owner
    filmed. So the read is claimed here, before the timer is set, and let go
    when it is done.
  */
  const holdProfileLoad = useCallback(() => {
    setProfileLoads(n => n + 1);
    let done = false;
    return () => {
      if (done) return;
      done = true;
      setProfileLoads(n => n - 1);
    };
  }, []);

  /*
    **Whether OnHand asked to be signed out.** The trace from the owner's browser
    on 7 September 2026 settles what was happening: he signs in, the app draws,
    and 218 milliseconds later a SIGNED_OUT arrives that nothing in OnHand asked
    for. Every place this app ends a session is recorded, and none of them fired.

    Supabase ends a session by itself when it decides a session it finds in
    storage is dead: it clears its keys and tells everybody it signed out. It
    does not distinguish the dead one it was cleaning up from the good one that
    replaced it a moment earlier, and the good one goes with it.

    So an unrequested SIGNED_OUT is not taken at face value any more. It is
    checked, once, against whether there is actually still a session.
  */
  const weAskedToSignOut = useRef(false);
  const profileRef   = useRef(null);
  // The moment somebody last did something, and a latch so a sign-out for
  // sitting still is only ever started once.
  const lastActivity = useRef(Date.now());
  const timingOut    = useRef(false);

  // A person's role is not a property of them any more - it is a property of
  // them *in a site*. Someone can be a Site Admin at one company and a Rep at
  // another, and profiles.role can only hold one answer.
  //
  // Rather than change the twenty-odd places that read profile.role, the role
  // is swapped for the one that applies to the site currently being viewed, at
  // the single point where the profile is loaded. Every existing check then
  // becomes site-aware without being touched.
  //
  // profile.role          the role here, in this site  <- what the app reads
  // profile.account_role  the one stored on the person, kept for reference
  /*
    **Only the newest load may set anything.** Two of these run at once on every
    sign-in: `signIn` starts one, and the SIGNED_IN event starts another. Whichever
    answers last used to win, so a slow answer to an old question could overwrite
    the right answer to the new one. Counting the loads and ignoring anything but
    the latest is what stops that.
  */
  const loadSeq = useRef(0);

  /*
    Load the profile, and **say which of three things happened**: it loaded, there
    is no such person, or the read did not work. Those are three different facts
    and this used to hand back one answer for all of them.

    **That was the fault behind "I sign in and nothing happens", found 7 September
    2026.** One of the profile reads on every sign-in came back 401, meaning the
    token was refused. This returned null for it, exactly as it would for somebody
    with no profile at all, so the caller below concluded the account was not
    allowed in, cleared the profile and signed him out. The app had already loaded
    the dashboard by then, so it bounced straight back to the sign-in screen with
    no message, because nothing had actually failed as far as the screen knew.

    **A failed read is not an answer about who somebody is.** It is the same
    family as the rest of this project's silent failures: an empty answer is not
    an error, and an error is not an empty answer.

    `maybeSingle` rather than `single` on purpose: `single` turns "no rows" into an
    error too, which is the very distinction this has to make.
  */
  /*
    **It asks again when the answer is nothing.** A read that races the token
    comes back with no rows and no complaint, and that happens on every sign-in,
    so one attempt is not an answer. Three tries a short beat apart, which is
    long enough for the browser to have stored the session and short enough that
    nobody watching the screen notices.
  */
  /*
    **A read on its way is counted**, so `App` can tell "still asking" apart from
    "asked and found nothing" and stop showing the alarming screen in front of a
    sign-in that is working. See `profileLoads` above.
  */
  async function loadProfile(userId, tries = 3) {
    setProfileLoads(n => n + 1);
    try {
      return await readProfile(userId, tries);
    } finally {
      setProfileLoads(n => n - 1);
    }
  }

  async function readProfile(userId, tries = 3) {
    const seq = (loadSeq.current += 1);
    const [{ data, error }, { data: siteRole }] = await Promise.all([
      supabase.from('profiles').select('*').eq('id', userId).maybeSingle(),
      supabase.rpc('current_org_role'),
    ]);
    const stale = seq !== loadSeq.current;
    trace('loadProfile', { seq, stale, tries, gotRow: !!data, error: error ? error.message : null, siteRole: siteRole || null });
    if (error) return { profile: null, failed: true, stale };
    /*
      **A good answer is never thrown away, however late it is. Corrected 7
      September 2026, and the earlier version of this line was the fault the
      owner reported six times.**

      Signing in starts two reads of the record; reloading the page starts one.
      That is the whole difference between the two, and it is exactly where the
      logs drew the line: reloading always worked and signing in never did.

      What happened every time was that the read signing in waited for SUCCEEDED,
      got the row, and then refused to use it because a second read had started
      after it. That second read failed. So nothing set the record, and a person
      with a valid session and a perfectly good account sat looking at the
      sign-in screen.

      The refusal was added hours earlier to stop a slow old answer overwriting a
      fresh one, and it was too broad. **Being late only matters when the answer
      is bad**, and these reads all ask the same question of the same row, so a
      late yes and an early yes are the same yes.
    */
    if (!data) {
      if (stale) return { profile: null, failed: false, stale };
      if (tries > 1) {
        await new Promise(r => setTimeout(r, 250));
        return readProfile(userId, tries - 1);
      }
      // Still nothing after asking three times. The profile is not cleared
      // here: `AppRoutes` already shows the sign-in screen without one, and
      // clearing it would throw away a session that may be perfectly good.
      return { profile: null, failed: false, stale };
    }

    // Falling back to the stored role matters: somebody with no membership yet
    // should not be locked out of the app while an admin sorts it out.
    const merged = { ...data, account_role: data.role, role: siteRole || data.role };
    setProfile(merged);
    return { profile: merged, failed: false, stale: false };
  }

  const signOut = useCallback(async (given = 'USER_LOGOUT') => {
    /*
      **A click is not a reason**, 18 September 2026 (the audit after the
      rebrand, issue 19). The shared frame's Sign out hands this the click event
      itself, and the note on the next line tried to write that event out, which
      cannot be done, so Sign out did nothing at all on the loading screens,
      Page not found and count entry's stop screens. Anything but a written
      reason is an ordinary sign-out.
    */
    const reason = signOutReason(given);
    // Which call ended the session is the one thing the server's logs cannot
    // show, and it is the whole question.
    trace('signOut', { reason, from: String(new Error().stack || '').split('\n').slice(1, 5).join(' | ') });
    weAskedToSignOut.current = true;
    setShowWarning(false);
    // Log before clearing profile
    const currentProfile = profileRef.current;
    if (currentProfile) {
      await logAudit(currentProfile, reason, 'auth', { target_name: currentProfile.full_name });
    }
    /*
      **Why they were signed out, left where the sign-in screen can read it.**
      Added 10 September 2026 for job 25, and it is the owner's own answer: asked
      whether the sign-in screen should say why somebody was thrown out mid-
      session, he said "yes."

      Until now the reason was written to the audit log and NEVER SHOWN TO THE
      PERSON IT HAPPENED TO. Somebody who stepped away for lunch came back to a
      sign-in screen with nothing on it, which is indistinguishable from being
      signed out for a fault, or from never having been signed in.

      **`sessionStorage`, not `localStorage`, and that is the whole design.** It
      is emptied when the tab closes, so the message appears on the way back from
      being thrown out and never on a fresh, deliberate visit. A reason that
      outlives the session it explains is a reason shown to the wrong person.

      **A person who pressed Sign Out themselves is told nothing**, because they
      know: they just pressed it.
    */
    try {
      if (reason && reason !== 'USER_LOGOUT') {
        sessionStorage.setItem('onhand.signedOutBecause', reason);
      }
    } catch { /* a browser refusing storage loses the explanation, not the sign-out */ }
    await supabase.auth.signOut();
    setUser(null);
    setProfile(null);
    sessionStorage.removeItem('adminTab');
  }, []);

  /*
    **Somebody did something.** Written down, and shared with any other tab of
    OnHand, so that leaving one tab alone while working in another is not two
    hours of doing nothing.

    Moving a mouse fires this hundreds of times a minute, so it is written down
    at most once a second. Storage can be refused outright by a browser set to
    block site data, which is why every touch of it is wrapped: the clock in this
    tab still works on its own.
  */
  const markActive = useCallback((now = Date.now()) => {
    lastActivity.current = now;
    try { window.localStorage.setItem(ACTIVITY_KEY, String(now)); } catch (e) { /* storage refused */ }
  }, []);

  const onActivity = useCallback(() => {
    const now = Date.now();
    if (now - lastActivity.current < ACTIVITY_THROTTLE_MS) return;
    markActive(now);
  }, [markActive]);

  /*
    The latest moment any tab saw somebody do something. A stored moment in the
    future is not believed, because a clock that has been put back would
    otherwise keep somebody signed in for ever.
  */
  const lastActivityAt = useCallback(() => {
    let stored = 0;
    try { stored = Number(window.localStorage.getItem(ACTIVITY_KEY)) || 0; } catch (e) { stored = 0; }
    if (stored > Date.now()) stored = 0;
    return Math.max(lastActivity.current, stored);
  }, []);

  /*
    **The clock, rather than an alarm.** See the note above `IDLE_TIMEOUT_MS` for
    why this is not two `setTimeout`s any more.

    It is keyed on the person's id rather than on the whole `user` object,
    because Supabase hands over a brand new object every time it renews the
    token, and starting this again on every renewal is exactly what stopped
    anybody ever being signed out.
  */
  const userId = user?.id || null;

  useEffect(() => {
    if (!userId) { setShowWarning(false); timingOut.current = false; return; }

    // Signing in, or opening the app with a session already stored, is
    // somebody doing something.
    timingOut.current = false;
    markActive();

    const tick = () => {
      const left = IDLE_TIMEOUT_MS - (Date.now() - lastActivityAt());
      if (left <= 0) {
        if (timingOut.current) return;
        timingOut.current = true;
        setShowWarning(false);
        signOut('SESSION_TIMEOUT');
        return;
      }
      if (left <= WARN_BEFORE_MS) {
        setShowWarning(true);
        setCountdown(Math.ceil(left / 1000));
      } else {
        setShowWarning(false);
      }
    };

    tick();
    const ticker = setInterval(tick, IDLE_TICK_MS);
    IDLE_EVENTS.forEach(e => window.addEventListener(e, onActivity, { passive: true }));
    return () => {
      clearInterval(ticker);
      IDLE_EVENTS.forEach(e => window.removeEventListener(e, onActivity));
    };
  }, [userId, signOut, onActivity, markActive, lastActivityAt]);

  // A session that belongs to a switched-off account is ended rather than
  // honoured. Signing out fires onAuthStateChange again with no session, which
  // just clears the profile - there is no loop.
  const dropIfNotAllowedIn = useCallback(async (userId) => {
    const { profile: prof, failed, stale } = await loadProfile(userId);
    /*
      **A read that did not work is not grounds for throwing anybody out**, and
      neither is an answer to a question that has already been asked again. Both
      used to end in a sign-out. Leaving the state alone is right in both cases:
      a newer load is either already running or the next one will run when the
      page is next opened.
    */
    const verdict = whatToDo({ profile: prof, failed, stale });
    trace('dropIfNotAllowedIn', { verdict, hasProfile: !!prof, failed, stale, isActive: prof ? prof.is_active : null });
    /*
      **Only "switched off" ends a session.** A read that broke, a late answer to
      a question already asked again, and an absence after three tries all leave
      everything exactly as it was. Signing out is what made this unrecoverable:
      the sign-in screen shows without a profile anyway, and destroying the
      session as well meant the next attempt started from nothing.
    */
    if (verdict !== 'out') return verdict === 'in' ? prof : null;
    setProfile(null);
    setUser(null);
    await supabase.auth.signOut();
    return null;
  }, []);

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      traceWho(session?.user?.id || null);
      trace('getSession', { hasSession: !!session, user: session?.user?.id || null });
      setUser(session?.user ?? null);
      if (session?.user) dropIfNotAllowedIn(session.user.id).finally(() => setLoading(false));
      else setLoading(false);
    });
    /*
      **Nothing may ask the database from inside this callback. Found 7
      September 2026, and it is the actual cause of "I sign in and nothing
      happens".**

      Supabase runs this while it is still holding its own lock on the session,
      and it says in its documentation not to call it from in here. The app was
      doing exactly that: reading the person's record from inside the callback.
      That read cannot see the session being stored, so it goes out with no
      token at all.

      **And the database answers a request with no token by hiding the row, not
      by objecting.** So the read came back empty, and empty was read as "this
      person has no record", and the person was signed out a moment after
      signing in.

      That is why it happened on every sign-in, why the dashboard flashed up
      first (it had already started loading), and why nothing appeared on screen
      to say what went wrong.

      **Stepping out of the callback before doing anything is the whole fix.** By
      the time the timer fires, the lock is released and the session is stored,
      so the read carries the token it should always have had.
    */
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      traceWho(session?.user?.id || null);
      trace('authEvent', { event: _event, hasSession: !!session, user: session?.user?.id || null });

      /*
        **Somebody who followed a reset link is here to set a password and
        nothing else.** Added 10 September 2026 with self-service reset.

        Following the link signs them in for real, so without this they would
        land on their home page with their old password still in force and no
        idea the link had done anything. Supabase announces `PASSWORD_RECOVERY`
        when the page opens with the token in the address, and this holds that
        fact so `App.jsx` can show the one screen that matters until it is done.
      */
      if (_event === 'PASSWORD_RECOVERY') setRecovering(true);

      /*
        **A sign-out nobody asked for is checked before it is believed.**

        Supabase cleans up a session it finds dead in storage by clearing its
        keys and announcing a sign-out, and it makes no distinction between the
        dead session it was tidying and the good one that replaced it moments
        before. On the owner's machine that arrived a fifth of a second after
        every successful sign-in and put him straight back on the sign-in screen,
        six times over, with nothing on screen to say why.

        So: if OnHand did not ask for it, ask the auth system whether there is
        still a session. If there is, the announcement was about the old one and
        is ignored. If there is not, it was real and is honoured.
      */
      if (_event === 'SIGNED_OUT' && !session && !weAskedToSignOut.current) {
        const release = holdProfileLoad();
        setTimeout(async () => {
          try {
            const { data } = await supabase.auth.getSession();
            trace('unaskedSignOut', { stillSignedIn: !!data?.session, user: data?.session?.user?.id || null });
            if (data?.session?.user) {
              setUser(data.session.user);
              await dropIfNotAllowedIn(data.session.user.id);
            } else {
              setUser(null);
              setProfile(null);
            }
          } finally { release(); }
        }, 0);
        return;
      }
      if (_event === 'SIGNED_OUT') weAskedToSignOut.current = false;

      setUser(session?.user ?? null);
      if (session?.user) {
        const release = holdProfileLoad();
        setTimeout(() => { dropIfNotAllowedIn(session.user.id).finally(release); }, 0);
      } else setProfile(null);
    });
    return () => subscription.unsubscribe();
  }, [dropIfNotAllowedIn, holdProfileLoad]);

  async function signIn(email, password) {
    trace('signIn:start', {});
    /*
      **Clear out whatever was there first.** A session left in storage from an
      earlier visit is what Supabase later decides is dead, and its tidying up
      is what took the new one with it. Removing it here, before signing in,
      means there is nothing left to be tidied. `scope: 'local'` touches only
      this browser and makes no network call.
    */
    weAskedToSignOut.current = true;   // this one IS ours, so let it through
    try { await supabase.auth.signOut({ scope: 'local' }); } catch { /* nothing there */ }
    weAskedToSignOut.current = false;
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    traceWho(data?.user?.id || null);
    trace('signIn:password', { ok: !error, error: error ? error.message : null, user: data?.user?.id || null });
    if (error) throw error;

    // The password was right. Whether the account is allowed in is a separate
    // question, and the answer lives in a table Supabase Auth cannot see.
    const { profile: prof, failed } = await loadProfile(data.user.id);
    /*
      **A read that did not work gets its own words**, because telling somebody
      their account is not set up when the truth is that the network hiccuped
      sends them to an administrator for a problem nobody has.
    */
    /*
      **Signing in complains only about what it positively knows.** Corrected 7
      September 2026, the moment the owner got in and reported a yellow box
      flashing before his home page appeared.

      That box was this function throwing because ITS read of the record did not
      work, while a second read, already running, was about to succeed. So the
      screen told him something had gone wrong a fraction of a second before
      everything went right.

      A read that did not work is not a fact about a person, which is the same
      lesson this evening taught three times. The only two things worth stopping
      for are a password the server rejected, which has already thrown above,
      and a record that says the account is switched off. Everything else
      settles on its own, and if it truly cannot, the screen that says
      "signed in, could not load your account" is there to say so properly.
    */
    if (prof && !isAllowedIn(prof)) {
      setProfile(null);
      setUser(null);
      await supabase.auth.signOut();
      throw new AccountInactiveError('This account has been deactivated. Contact your administrator.');
    }
    if (!prof) {
      trace('signIn:noProfileYet', { failed });
      return data;   // the load still running will settle it
    }

    await logAudit(prof, 'USER_LOGIN', 'auth', { target_name: data.user.email });
    // Clear saved tab so every login lands on the correct default:
    // admin/manager goes to 'overview' (Count Cycle Details)
    // rep goes to 'mycounts' (My Counts)
    trace('signIn:done', { role: prof.role, accountRole: prof.account_role });
    try {
      sessionStorage.removeItem('adminTab');
      sessionStorage.removeItem('managerTab');
      sessionStorage.removeItem('repTab');
    } catch (e) { trace('signIn:storageRefused', { why: String(e && e.message) }); }
    window.history.replaceState(null, '', '/');
    return data;
  }

  function stayLoggedIn() {
    markActive();
    setShowWarning(false);
  }

  return (
    <AuthContext.Provider value={{ user, profile, loading, profileLoading: profileLoads > 0, signIn, signOut, recovering }}>
      {children}

      {/*
        **"Still there?"** lives in its own file so it can be photographed. See
        `IdleWarning.jsx` for what it is and where the drawing came from.
      */}
      {showWarning && (
        <IdleWarning
          secondsLeft={countdown}
          idleFor={idleWarningWords()}
          onStay={stayLoggedIn}
          onSignOut={() => signOut('USER_LOGOUT')}
        />
      )}

    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
